import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { VisuallyHidden, Heading, PillText, SVGIcon } from '@web/atoms';
import { ResponsiveImage, CtaButton } from '@web/molecules';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import freeShippingTruckImage from '../../images/free-shipping-truck.svg';
import moneyBackGuaranteeImage from '../../images/money-back-guarantee.svg';
import { CTAGroup } from '../CTAGroup';
import {
  Wrapper,
  StarRating,
  Subheadline,
  Content,
  RightSideContent,
  ImageWrapper,
  ButtonWrapper,
  MealPlanRow,
  MealPlanBody,
  MealPlanHeadline,
  MealPlanDescription,
  PillTextWrapper,
  StyledGuarantee,
  StyledGuaranteesContainer,
  Testimonial,
  TestimonialImage,
  PupDesc,
  PupInfo,
  PupName,
  TestimonialDescription,
  TestimonialComment,
} from './styles';

export const TryMyOllie = ({
  headline,
  subheadline,
  mealPlanTypes,
  testimonials,
  image,
  cta,
  typography = 'heading3',
  backgroundColor = 'background.base',
  id,
  reviewsCopy,
  starColor,
}) => {
  const { formattedAmount } = useShopifyOfferContext();

  return (
    <Wrapper id={id} $backgroundColor={backgroundColor}>
      <StarRating rating={5} $backgroundColor={starColor}>
        {reviewsCopy}
        <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
      </StarRating>
      <Heading
        position={'center'}
        headingLevel={'h3'}
        typography={typography}
        headingText={headline}
        backgroundColor={backgroundColor}
      />
      <Subheadline>
        {interpolate(subheadline, { offer: formattedAmount })}
      </Subheadline>
      <Content>
        <ImageWrapper>
          <ResponsiveImage {...image} />
        </ImageWrapper>
        <RightSideContent>
          {mealPlanTypes.map((mealplan, index) => {
            return (
              <MealPlanRow key={index} $backgroundColor={backgroundColor}>
                <ResponsiveImage {...mealplan.img} />
                <MealPlanBody>
                  <PillTextWrapper>
                    <PillText
                      size={'small'}
                      text={interpolate(mealplan.benefit, {
                        offer: formattedAmount,
                      })}
                      variant={mealplan.benefitVariant || 'dark'}
                      icon={mealplan.benefitIcon}
                      iconAlignment={mealplan.benefitIconAlignment || 'left'}
                    />
                  </PillTextWrapper>
                  <MealPlanHeadline>{mealplan.title}</MealPlanHeadline>
                  <MealPlanDescription>
                    {interpolate(mealplan.description, {
                      offer: formattedAmount,
                    })}
                  </MealPlanDescription>
                </MealPlanBody>
              </MealPlanRow>
            );
          })}
          {cta &&
            (cta.component === 'cta_group' ? (
              <ButtonWrapper>
                <CTAGroup {...cta} />
              </ButtonWrapper>
            ) : (
              <ButtonWrapper>
                <CtaButton
                  {...cta}
                  fullWidth={true}
                  href={cta.target}
                  variant={cta.variant}
                  icon={cta.icon}
                  iconAlignment={cta.iconAlignment}
                  headerText={cta.headerText}
                >
                  {cta.text}
                </CtaButton>
              </ButtonWrapper>
            ))}
          <StyledGuaranteesContainer>
            <StyledGuarantee>
              <SVGIcon
                height={'18px'}
                width={'24px'}
                viewBox="0 0 24 18"
                icon={freeShippingTruckImage}
                fillColor="text.primary"
              />
              <p>FREE Shipping</p>
            </StyledGuarantee>
            <StyledGuarantee>
              <SVGIcon
                height={'20px'}
                width={'20px'}
                viewBox="0 0 20 17"
                icon={moneyBackGuaranteeImage}
                fillColor="text.primary"
              />
              <p>Money back guarantee</p>
            </StyledGuarantee>
          </StyledGuaranteesContainer>
          {testimonials?.map((testimonial, index) => {
            return (
              <Testimonial key={index}>
                <TestimonialDescription>
                  <TestimonialImage>
                    {testimonial.asset && (
                      <ResponsiveImage {...testimonial.asset} />
                    )}
                  </TestimonialImage>
                  <PupInfo>
                    <PupName>{testimonial.name}</PupName>
                    <PupDesc>
                      <p>{testimonial.description}</p>
                    </PupDesc>
                    <StarRating rating={5} $backgroundColor={starColor}>
                      <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
                    </StarRating>
                  </PupInfo>
                </TestimonialDescription>
                <TestimonialComment>{testimonial.body}</TestimonialComment>
              </Testimonial>
            );
          })}
        </RightSideContent>
      </Content>
    </Wrapper>
  );
};

TryMyOllie.displayName = 'TryMyOllie';
TryMyOllie.propTypes = {
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string,
  mealPlanTypes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      benefit: PropTypes.string,
      benefitVariant: PropTypes.string,
      benefitIcon: PropTypes.shape({
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      benefitIconAlignment: PropTypes.oneOf(['left', 'right']),
      description: PropTypes.string,
      img: PropTypes.shape({
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
          .isRequired,
        alt: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ),
  testimonials: PropTypes.array,
  image: PropTypes.object.isRequired,
  cta: PropTypes.object,
  id: PropTypes.string,
  reviewsCopy: PropTypes.string,
  starColor: PropTypes.string,
};
